import { stringify } from 'querystring'

import clsx from 'clsx'
import React, { useEffect, useRef, useState } from 'react'
import Head from 'next/head'
import { useRouter } from 'next/router'

import { Text } from '@wartek-id/text'
import { Button } from '@wartek-id/button'
import { Shimmer } from '@wartek-id/shimmer'
import {
  HeaderBar,
  HeaderBarLeftAction,
  HeaderBarRightAction,
  HeaderBarTitle,
} from '@wartek-id/header-bar'
import { Icon } from '@wartek-id/icon'
import { historyUtils } from '@wartek-id/fe-toolbox'

import { useScrollPosition } from 'utils/hooks/useScrollPosition'
import {
  PDFReader,
  ViewMeta,
  ViewHeading,
  FeedbackList,
} from 'app/PelatihanMandiriPage/partials/AksiNyata'
import AndroidHandler from 'utils/androidHandler'
import { ANDROID_VERSION_MAP } from 'configs/androiVersionMap'
import ViewAksiNyataPlaceholder from 'app/PelatihanMandiriPage/partials/AksiNyata/Placeholder'
import { useAuth } from 'utils/auth'
import { EmptyState, EmptyStateAction } from 'components/EmptyState'
import { SocialShareDrawer } from 'components/SocialShareDrawer'
import {
  useGetAksiNyataFeedback,
  useGetPublicAksiNyata,
} from 'api/microlearning/useAPIAksiNyata'
import { WithLayoutListener } from 'components/Layout/Desktop/DesktopLayout'

const AksiNyataPage = (props) => {
  const { query, replace, back, push } = useRouter()

  const { isDesktopScreenSize } = props

  const currentAksiNyata = useGetPublicAksiNyata(query.id as string)
  const currentFeedback = useGetAksiNyataFeedback(
    query.id as string,
    currentAksiNyata.isSuccess
  )

  const [showShareDialog, setShowShareDialog] = useState(false)
  const [hasFeedback, setHasFeedback] = useState(false)
  const shareText = useRef('')
  const { session } = useAuth()
  const androidHandler = AndroidHandler()
  const readerRef = React.useRef<HTMLDivElement>(null)
  const [isSticky, setSticky] = React.useState(false)

  useScrollPosition({
    effect: ({ currPos }) => {
      if (readerRef.current?.offsetTop + currPos.y - 64 <= 0) {
        setSticky(true)
      } else {
        if (isSticky === true) {
          setSticky(false)
        }
      }
    },
  })

  useEffect(() => {
    if (currentFeedback.isSuccess) {
      const currentUserFeedback = currentFeedback.data.data.find(
        (feedback) => feedback?.author?.id === session?.user?.id
      )
      setHasFeedback(typeof currentUserFeedback !== 'undefined')
    }
  }, [session?.user?.id, currentFeedback.isSuccess, currentFeedback.data])

  useEffect(() => {
    if (currentAksiNyata.isSuccess) {
      if (
        currentAksiNyata.data?.data?.creator?.email === session?.user?.email
      ) {
        shareText.current =
          `Bapak/Ibu Guru, saya melakukan *Aksi Nyata* sebagai implementasi ` +
          `pemahaman saya terhadap salah satu topik Pelatihan Mandiri di ` +
          `platform Merdeka Mengajar \n\n` +
          `Mohon bantuan Bapak/Ibu Guru untuk memberikan ` +
          `tanggapan di tautan berikut. Terima kasih.`
      } else {
        shareText.current =
          `Bapak/Ibu Guru, berikut ada Aksi Nyata rekan sejawat ` +
          `mengenai ${currentAksiNyata.data?.data?.title} \n\n` +
          `Beliau membutuhkan tanggapan untuk Aksi Nyata ini, ` +
          `mari kita bantu tanggapi di tautan berikut.` +
          ` Terima kasih.`
      }
    }
  }, [session, currentAksiNyata.data, currentAksiNyata.isSuccess])

  const shareUrl =
    `${process.env.NEXT_PUBLIC_BASE_URL}/${
      process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH || 'pelatihan-mandiri'
    }/aksi-nyata/${query.id}?` +
    stringify({
      topik_name: query.topik_name,
      topik_id: query.topik_id,
    })

  const triggerShareDialog = () => {
    if (androidHandler.enabled) {
      androidHandler.shareToolkitUrl(`${shareText.current}  ${shareUrl}`)
    } else {
      setShowShareDialog(true)
    }
  }

  const triggerRefecthComment = () => {
    currentFeedback.refetch()
  }

  const shouldShowFeedbackField = ['COMPLETED', 'VALIDATED'].includes(
    currentAksiNyata.data?.data?.state
  )

  return (
    <WithLayoutListener isDesktop={isDesktopScreenSize}>
      <>
        <Head>
          <title>
            Guru Mengajar | Aksi nyata {currentAksiNyata.data?.data?.title}
          </title>
        </Head>
        <HeaderBar fixed className="z-10">
          <HeaderBarLeftAction
            icon={<Icon>arrow_back</Icon>}
            onClick={() => {
              if (
                androidHandler.enabled &&
                androidHandler.getVersion() >=
                  ANDROID_VERSION_MAP.PELATIHAN_MANDIRI_NATIVE
              ) {
                androidHandler.backToApp()
              } else {
                historyUtils.backToApp(back)
              }
            }}
          />
          <HeaderBarTitle>Aksi Nyata</HeaderBarTitle>
          <HeaderBarRightAction onClick={triggerShareDialog}>
            <Icon>share</Icon>
          </HeaderBarRightAction>
        </HeaderBar>
        <div
          className={clsx(
            'relative py-5 z-0',
            isDesktopScreenSize && 'bg-white rounded-md'
          )}
          style={{
            minHeight: 'calc(100vh - 3.5rem)',
          }}
        >
          {currentAksiNyata.isSuccess ? (
            <>
              <ViewHeading
                title={currentAksiNyata.data.data?.title}
                author={currentAksiNyata.data.data?.creator}
                topikId={query.topik_id as string}
                topikName={query.topik_name as string}
                state={currentAksiNyata.data.data?.state}
                reviewer={{
                  reviewerName: currentAksiNyata.data.data?.reviewerName || '',
                  reviewerUserID:
                    currentAksiNyata.data.data?.reviewerUserID || '',
                }}
                selectedTitle={
                  currentAksiNyata.data.data?.selectedOption?.title
                }
                showCta={
                  session?.user?.id !==
                    currentAksiNyata.data?.data?.creator?.id &&
                  session?.user?.email !==
                    currentAksiNyata.data?.data?.creator?.email
                }
              />
              <ViewMeta
                description={currentAksiNyata.data.data?.description}
                reflection={currentAksiNyata.data.data?.reflection}
              />
              {currentAksiNyata.data.data?.attachments?.[0].type ===
                'video' && (
                <div className="p-4">
                  <Button
                    as="a"
                    target="_blank"
                    href={currentAksiNyata.data.data?.attachments?.[0].url}
                    size="lg"
                    color="black"
                    fullWidth
                  >
                    Lihat Video
                  </Button>
                </div>
              )}
              {currentAksiNyata.data.data?.attachments?.[0].type === 'pdf' && (
                <div className="p-4">
                  <Text className="mb-2" variant="heading-sm">
                    Dokumen
                  </Text>
                  <div ref={readerRef}>
                    <PDFReader
                      isSticky={isSticky}
                      url={currentAksiNyata.data.data?.attachments?.[0].url}
                    />
                  </div>
                </div>
              )}
            </>
          ) : currentAksiNyata.isLoading ? (
            <ViewAksiNyataPlaceholder />
          ) : (
            <EmptyState
              title="Tidak Dapat Ditampilkan"
              description="Anda tidak memiliki akses untuk melihat Aksi Nyata milik guru lain"
            >
              <EmptyStateAction
                onClick={() =>
                  replace(
                    `/${
                      process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH ||
                      'pelatihan-mandiri'
                    }`
                  )
                }
              >
                Kembali ke daftar Topik
              </EmptyStateAction>
            </EmptyState>
          )}

          {currentFeedback.isLoading && (
            <Shimmer
              className="mb-2"
              height={16}
              noOfLines={4}
              style={{ width: '100%' }}
            />
          )}

          {currentFeedback.isSuccess && shouldShowFeedbackField && (
            <FeedbackList
              aksiNyataId={query.id as string}
              topikId={query.topik_id as string}
              topikName={query.topik_name as string}
              hasFeedback={hasFeedback}
              isOwner={
                session?.user?.id ===
                  currentAksiNyata.data?.data?.creator?.id ||
                session?.user?.email ===
                  currentAksiNyata.data?.data?.creator?.email
              }
              feedbacks={currentFeedback.data?.data}
              feedbackFetchStatus={currentFeedback.status}
              onShareButtonClick={triggerShareDialog}
              onGotoFormClick={async () => {
                await push(
                  `/${
                    process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH ||
                    'pelatihan-mandiri'
                  }/aksi-nyata/${query.id}/form?` +
                    `title=${currentAksiNyata.data?.data?.title}`
                )
              }}
              onRefreshFeedback={triggerRefecthComment}
            />
          )}
        </div>
        <SocialShareDrawer
          isOpen={showShareDialog}
          title="Bagikan Aksi Nyata"
          onClose={() => {
            setShowShareDialog(false)
          }}
          linkOnlyOnCopy={true}
          shareText={shareText.current}
          shareUrl={shareUrl}
        />
      </>
    </WithLayoutListener>
  )
}

AksiNyataPage.layoutOpts = {
  hasHeader: true,
  enableDesktopLayout: true,
}

export default AksiNyataPage
